import { useSearchBrandsLogosMutation } from "@api/media";
import { useEffect, useRef, useState } from "react";
import type { IBrandfetchBrand } from "@/shared/types";
import type { IMediaLibLogo } from "../types";
import { mapMediaLibLogoFinderData } from "../utils";

interface IProps {
  logosData: IMediaLibLogo[];
  setLogosData: (value: IMediaLibLogo[]) => void;
  brand: IBrandfetchBrand | null;
  setBrand: (value: IBrandfetchBrand | null) => void;
  setSearchQuery: (value: string) => void;
}

export const useMediaLibFetchLogos = ({
  setSearchQuery,
  logosData,
  setLogosData,
  brand,
  setBrand,
}: IProps) => {
  const initLoad = useRef(true);
  const [fetchLogos, { isLoading }] = useSearchBrandsLogosMutation();
  const [isError, setIsError] = useState(false);
  const [isRateLimitError, setIsRateLimitError] = useState(false);
  const [reloadKey, setReloadKey] = useState({});

  useEffect(() => {
    if (initLoad.current) {
      initLoad.current = false;
      if (logosData.length > 0) {
        return;
      }
    }
    if (!brand) return;

    const controller = new AbortController();
    (async () => {
      try {
        setIsError(false);
        setIsRateLimitError(false);

        const data = await fetchLogos({
          domain: brand.domain,
          signal: controller.signal,
        }).unwrap();

        if (data.limits.current >= data.limits.limit) {
          setIsRateLimitError(true);
          return;
        }

        setLogosData(mapMediaLibLogoFinderData(data, brand.domain));
      } catch (e) {
        console.log(e);
        setIsError(true);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [brand, setLogosData, reloadKey]);

  const clearData = () => {
    setBrand(null);
    setSearchQuery("");
    setLogosData(
      Array.from({ length: 14 }, (_, idx) => {
        const src = `${window.location.origin}/assets/images/brand-logos/${idx}.png`;
        return {
          type: "logo",
          color: "color",
          src,
          originalSrc: src,
        };
      }),
    );
  };

  useEffect(() => {
    if (brand || logosData.length > 0) return;
    clearData();
  }, [brand, setLogosData, reloadKey]);

  return {
    isError,
    isRateLimitError,
    clearData,
    reloadData: () => setReloadKey({}),
    isLoading,
  };
};
